import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";

import { ModalController } from "@ionic/angular";
import { Observable, of } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { AuthService, StorageService, UserService } from "../services";

@Injectable({
  providedIn: "root",
})
export class AuthGuard {
  constructor(
    private authService: AuthService,
    private userService: UserService,
    private storageService: StorageService,
    private router: Router
  ) {
    // console.log('AuthGuard initialized');
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // console.log('canActivate called', { url: state.url });
    // set access token if it is in the url parameters
    this.handleAccessTokenFromFragment(route);
    return this.checkAuthentication();
  }

  private handleAccessTokenFromFragment(route: ActivatedRouteSnapshot): void {
    // console.log('Handling access token from fragment');
    if (!route.fragment) {
      // console.log('No fragment found');
      return;
    }

    const accessTokenFragment = route.fragment
      .split("&")
      .find((f) => f.startsWith("access_token"));
    const accessToken = accessTokenFragment?.replace("access_token=", "");

    if (accessToken) {
      // console.log('Access token found in fragment, setting token and navigating to root');
      this.userService.setAccessToken(accessToken);
      this.router.navigate(["/"]);
    } else {
      // console.log('No access token found in fragment');
    }
  }

  private checkAuthentication(): Observable<boolean> {
    // console.log('Checking authentication');
    return this.userService.accessToken$.pipe(
      // tap(token => console.log('Current access token:', token)),
      switchMap(this.handleAccessToken.bind(this)),
      filter(Boolean),
      map(() => true),
      take(1)
      // tap(result => console.log('Authentication check result:', result))
    );
  }

  private handleAccessToken(
    accessToken: string | null
  ): Observable<boolean | null> {
    // If token already present in state, proceed as usual
    if (accessToken) {
      return this.storageService.get("isNotLinkedUser").pipe(
        switchMap(this.handleNotLinkedUser.bind(this)),
        switchMap(this.loadUserInfo.bind(this))
      );
    }

    // No token in state: try restore from persistent storage first
    return this.storageService.get("accessToken").pipe(
      take(1),
      switchMap((storedToken) => {
        if (storedToken) {
          this.userService.setAccessToken(storedToken);
          return this.storageService.get("isNotLinkedUser").pipe(
            switchMap(this.handleNotLinkedUser.bind(this)),
            switchMap(this.loadUserInfo.bind(this))
          );
        } else {
          this.authService.login();
          return of(null);
        }
      })
    );
  }

  private handleNotLinkedUser(isNotLinkedUser: string): Observable<boolean> {
    // console.log('Handling not linked user:', isNotLinkedUser);
    if (isNotLinkedUser === "true") {
      // console.log('User is not linked, logging out');
      this.authService.logout();
      return of(false);
    }
    return of(true);
  }

  private loadUserInfo(): Observable<any> {
    // console.log('Loading user info');

    return this.authService.isSetAccessToken$.pipe(
      // tap(isSet => console.log('Is access token set:', isSet)),
      switchMap(() => this.storageService.get("currentUser")),
      // tap(currentUser => console.log('Current user from storage:', currentUser)),
      switchMap(() => {
        // console.log('Getting current user');
        this.userService.getCurrentUser();
        return this.userService.currentUser$;
      })
      // tap(user => console.log('Current user:', user))
    );
  }
}
